import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Scale, RefreshCw, ChevronRight, ChevronDown, Plus, X } from 'lucide-react';
import api from './axiosConfig';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useFontSize } from './FontSizeContext';
import { exportToExcel } from './exportUtils';
import SmartPrintButton from './PrintButton';
import { translateAccountDescription } from './glDefaultAccounts';

const todayStr = () => new Date().toISOString().substring(0, 10);
const calendarYearBounds = () => {
  const y = new Date().getFullYear();
  return { start: `${y}-01-01`, end: `${y}-12-31`, name: String(y) };
};

const NewFiscalYearModal = ({ orgId, onClose, onSaved, t }) => {
  const defaults = calendarYearBounds();
  const [yearName, setYearName] = useState(defaults.name);
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!yearName || !startDate || !endDate) return;
    setSaving(true);
    try {
      await api.post('/gl/fiscal-years/save', { org_id: orgId, year_name: yearName, start_date: startDate, end_date: endDate });
      toast.success(t('common.saved', 'Saved'));
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.save_error', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[999] flex items-center justify-center" onClick={onClose}>
      <div className="bg-surface-card rounded-sm shadow-lg w-96 p-5" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-black text-primary-dark uppercase">{t('gl.fiscal_year.new', 'New Fiscal Year')}</h3>
          <X size={18} className="cursor-pointer text-ink-secondary" onClick={onClose} />
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col">
            <label className="text-[9px] text-ink-secondary font-black">{t('gl.fiscal_year.name', 'Year Name')}</label>
            <input className="border-b-2 border-line-subtle font-bold text-primary outline-none py-1" value={yearName} onChange={e => setYearName(e.target.value)} />
          </div>
          <div className="flex flex-col">
            <label className="text-[9px] text-ink-secondary font-black">{t('gl.fiscal_year.start', 'Start Date')}</label>
            <input type="date" className="border-b-2 border-line-subtle font-bold text-primary outline-none py-1" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div className="flex flex-col">
            <label className="text-[9px] text-ink-secondary font-black">{t('gl.fiscal_year.end', 'End Date')}</label>
            <input type="date" className="border-b-2 border-line-subtle font-bold text-primary outline-none py-1" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
          <button
            disabled={saving}
            onClick={handleSave}
            className="mt-2 bg-primary text-white font-black uppercase py-2 rounded-sm hover:bg-primary-dark transition-colors disabled:opacity-50"
          >
            {t('common.save', 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
};

const TrialBalance = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const { fontSize } = useFontSize();

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const [orgId, setOrgId] = useState(session.org_id || '');
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState(todayStr());
  const [showZero, setShowZero] = useState(false);
  const [showGroups, setShowGroups] = useState(true);
  const [rows, setRows] = useState([]);
  const [grand, setGrand] = useState({ opening_debit: 0, opening_credit: 0, debit: 0, credit: 0, closing_debit: 0, closing_credit: 0 });
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [showNewFY, setShowNewFY] = useState(false);

  const fetchFiscalYears = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await api.get('/gl/fiscal-years', { params: { org_id: orgId } });
      const years = res.data.data || [];
      setFiscalYears(years);
      if (years.length > 0 && !selectedFY) {
        const current = years.find(y => y.start_date <= todayStr() && y.end_date >= todayStr()) || years[0];
        setSelectedFY(String(current.id));
        setFromDate(current.start_date);
        setToDate(current.end_date);
      }
    } catch (err) {
      // صامت - المؤسسة ممكن ماعندهاش سنوات مالية لسه
    }
  }, [orgId]);

  useEffect(() => { fetchFiscalYears(); }, [fetchFiscalYears]);

  const handleFYChange = (id) => {
    setSelectedFY(id);
    const fy = fiscalYears.find(y => String(y.id) === id);
    if (fy) {
      setFromDate(fy.start_date);
      setToDate(fy.end_date);
    }
  };

  const fetchData = useCallback(async () => {
    if (!orgId || !toDate) return;
    setLoading(true);
    try {
      const res = await api.get('/gl/trial-balance', {
        params: { org_id: orgId, from_date: fromDate || undefined, to_date: toDate, show_zero: showZero, show_groups: showGroups }
      });
      setRows(res.data.data || []);
      setGrand({
        opening_debit: res.data.opening_debit, opening_credit: res.data.opening_credit,
        debit: res.data.debit, credit: res.data.credit,
        closing_debit: res.data.closing_debit, closing_credit: res.data.closing_credit,
      });
    } catch (err) {
      toast.error(t('common.load_error', 'Failed to load trial balance'));
    } finally {
      setLoading(false);
    }
  }, [orgId, fromDate, toDate, showZero, showGroups, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 🎯 نفس زرار "تصدير البيانات" الموجود بالفعل في AppSidebar لأي شاشة -
  // بيدوس على GLOBAL_EXPORT، إحنا بس بنسمع له هنا ونصدّر الميزان الحالي
  useEffect(() => {
    const columnsMap = {
      glaccount: t('gl.trial_balance.fields.account', 'Account'),
      description: t('gl.trial_balance.fields.description', 'Description'),
      account_type: t('gl.trial_balance.fields.type', 'Type'),
      opening_debit: t('gl.trial_balance.fields.opening_dr', 'Opening (Dr)'),
      opening_credit: t('gl.trial_balance.fields.opening_cr', 'Opening (Cr)'),
      debit: t('gl.trial_balance.fields.debit', 'Debit'),
      credit: t('gl.trial_balance.fields.credit', 'Credit'),
      closing_debit: t('gl.trial_balance.fields.closing_dr', 'Closing (Dr)'),
      closing_credit: t('gl.trial_balance.fields.closing_cr', 'Closing (Cr)'),
    };
    const handleExport = () => exportToExcel('trial-balance', {
      org_id: orgId, from_date: fromDate, to_date: toDate, show_zero: showZero, show_groups: showGroups
    }, columnsMap, t);
    window.addEventListener('GLOBAL_EXPORT', handleExport);
    return () => window.removeEventListener('GLOBAL_EXPORT', handleExport);
  }, [orgId, fromDate, toDate, showZero, showGroups, t]);

  // 🎯 الصفوف جاية flat مرتبة شجريًا (parent متبوع بكل أبنائه) مع level -
  // هنا بس بنفلتر أي صف تحت مجموعة متقفلة (collapsed) في تمريرة واحدة
  const visibleRows = useMemo(() => {
    const out = [];
    let hideBelowLevel = null;
    for (const row of rows) {
      if (hideBelowLevel !== null) {
        if (row.level > hideBelowLevel) continue;
        hideBelowLevel = null;
      }
      out.push(row);
      if (row.is_group && collapsed.has(row.glaccount)) {
        hideBelowLevel = row.level;
      }
    }
    return out;
  }, [rows, collapsed]);

  const toggleCollapse = (glaccount) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(glaccount)) next.delete(glaccount); else next.add(glaccount);
      return next;
    });
  };

  const fmt = (v) => (v ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');

  return (
    <div className={`flex flex-col h-full bg-surface-app uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo' : 'font-sans'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      {showNewFY && (
        <NewFiscalYearModal
          orgId={orgId}
          t={t}
          onClose={() => setShowNewFY(false)}
          onSaved={() => { setShowNewFY(false); setSelectedFY(''); fetchFiscalYears(); }}
        />
      )}

      <div className="bg-surface-card p-3 border-b shadow-sm z-20">
        <div className="flex justify-between items-center px-4 flex-wrap gap-3">
          <h2 className="text-primary-dark font-black italic text-lg flex items-center gap-2">
            <Scale size={22} className="text-status-warning" />
            {t('gl.trial_balance.title', 'Trial Balance')}
            <RefreshCw size={18} className={`cursor-pointer transition-all ${loading ? 'animate-spin text-status-warning' : 'text-primary'} ${isRTL ? 'mr-2' : 'ml-2'}`} onClick={fetchData} />
            <SmartPrintButton
              moduleName="TRIAL_BALANCE"
              extraParams={{ org_id: orgId, from_date: fromDate, to_date: toDate, show_zero: showZero, show_groups: showGroups }}
            />
          </h2>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex flex-col">
              <label className="text-[9px] text-ink-secondary font-black">{t('gl.trial_balance.org', 'Organization')}</label>
              <input className="border-b-2 border-line-subtle font-bold text-primary outline-none py-1 uppercase w-24" value={orgId} onChange={e => { setOrgId(e.target.value.toUpperCase()); setSelectedFY(''); }} />
            </div>
            <div className="flex flex-col">
              <label className="text-[9px] text-ink-secondary font-black">{t('gl.fiscal_year.title', 'Fiscal Year')}</label>
              <div className="flex items-center gap-1">
                <select
                  className="border-b-2 border-line-subtle font-bold text-primary outline-none py-1 bg-transparent normal-case"
                  value={selectedFY}
                  onChange={e => handleFYChange(e.target.value)}
                >
                  <option value="">{t('gl.fiscal_year.custom', 'Custom Range')}</option>
                  {fiscalYears.map(fy => (
                    <option key={fy.id} value={fy.id}>{fy.year_name}</option>
                  ))}
                </select>
                <Plus size={16} className="cursor-pointer text-primary shrink-0" onClick={() => setShowNewFY(true)} />
              </div>
            </div>
            <div className="flex flex-col">
              <label className="text-[9px] text-ink-secondary font-black">{t('gl.ledger.from', 'From')}</label>
              <input type="date" className="border-b-2 border-line-subtle font-bold text-primary outline-none py-1" value={fromDate} onChange={e => { setFromDate(e.target.value); setSelectedFY(''); }} />
            </div>
            <div className="flex flex-col">
              <label className="text-[9px] text-ink-secondary font-black">{t('gl.ledger.to', 'To')}</label>
              <input type="date" className="border-b-2 border-line-subtle font-bold text-primary outline-none py-1" value={toDate} onChange={e => { setToDate(e.target.value); setSelectedFY(''); }} />
            </div>
            <label className="flex items-center gap-1 text-[10px] font-black text-ink-secondary cursor-pointer normal-case">
              <input type="checkbox" checked={showGroups} onChange={e => setShowGroups(e.target.checked)} />
              {t('gl.trial_balance.show_groups', 'Show Group Accounts')}
            </label>
            <label className="flex items-center gap-1 text-[10px] font-black text-ink-secondary cursor-pointer normal-case">
              <input type="checkbox" checked={showZero} onChange={e => setShowZero(e.target.checked)} />
              {t('gl.trial_balance.show_zero', 'Show Zero Values')}
            </label>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 custom-scrollbar">
        <div className="bg-surface-card border rounded-sm shadow-sm overflow-hidden">
          <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
            <thead className="bg-primary-dark text-white font-bold uppercase sticky top-0 z-10">
              <tr>
                <th className="p-3 border-x border-white/10">{t('gl.trial_balance.fields.account', 'Account')}</th>
                <th className="p-3 border-x border-white/10 text-center w-28">{t('gl.trial_balance.fields.opening_dr', 'Opening (Dr)')}</th>
                <th className="p-3 border-x border-white/10 text-center w-28">{t('gl.trial_balance.fields.opening_cr', 'Opening (Cr)')}</th>
                <th className="p-3 border-x border-white/10 text-center w-28">{t('gl.trial_balance.fields.debit', 'Debit')}</th>
                <th className="p-3 border-x border-white/10 text-center w-28">{t('gl.trial_balance.fields.credit', 'Credit')}</th>
                <th className="p-3 border-x border-white/10 text-center w-28">{t('gl.trial_balance.fields.closing_dr', 'Closing (Dr)')}</th>
                <th className="p-3 border-x border-white/10 text-center w-28">{t('gl.trial_balance.fields.closing_cr', 'Closing (Cr)')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle font-bold">
              {loading ? (
                <tr><td colSpan="7" className="p-20 text-center font-black text-ink-secondary animate-pulse tracking-[0.2em]">{t('common.syncing', 'Syncing Data...')}</td></tr>
              ) : visibleRows.length === 0 ? (
                <tr><td colSpan="7" className="p-20 text-center font-black text-ink-secondary italic uppercase">{t('gl.trial_balance.no_records', 'No posted entries yet')}</td></tr>
              ) : (
                visibleRows.map(r => (
                  <tr key={r.glaccount} className={`hover:bg-primary-soft ${r.is_group ? 'bg-surface-panel/60' : ''}`}>
                    <td className="p-3 border-x border-line-subtle text-primary">
                      <div className="flex items-center gap-1" style={{ [isRTL ? 'paddingRight' : 'paddingLeft']: `${r.level * 18}px` }}>
                        {r.is_group ? (
                          <span className="cursor-pointer shrink-0" onClick={() => toggleCollapse(r.glaccount)}>
                            {collapsed.has(r.glaccount) ? <ChevronRight size={14} className={isRTL ? 'rotate-180' : ''} /> : <ChevronDown size={14} />}
                          </span>
                        ) : <span className="w-[14px] shrink-0" />}
                        <span className={r.is_group ? 'font-black' : ''}>{r.glaccount}</span>
                        <span className="text-ink normal-case italic font-normal text-ink-secondary">- {translateAccountDescription({ glaccount: r.glaccount, org_id: orgId, description: r.description }, t)}</span>
                      </div>
                    </td>
                    <td className="p-3 border-x border-line-subtle text-center text-status-success">{fmt(r.opening_debit)}</td>
                    <td className="p-3 border-x border-line-subtle text-center text-status-danger">{fmt(r.opening_credit)}</td>
                    <td className="p-3 border-x border-line-subtle text-center text-status-success">{fmt(r.debit)}</td>
                    <td className="p-3 border-x border-line-subtle text-center text-status-danger">{fmt(r.credit)}</td>
                    <td className="p-3 border-x border-line-subtle text-center text-status-success">{fmt(r.closing_debit)}</td>
                    <td className="p-3 border-x border-line-subtle text-center text-status-danger">{fmt(r.closing_credit)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {visibleRows.length > 0 && (
              <tfoot>
                <tr className="bg-surface-panel font-black">
                  <td className="p-3 border-x border-line-subtle">{t('common.total', 'Total')}</td>
                  <td className="p-3 border-x border-line-subtle text-center text-status-success">{fmt(grand.opening_debit)}</td>
                  <td className="p-3 border-x border-line-subtle text-center text-status-danger">{fmt(grand.opening_credit)}</td>
                  <td className="p-3 border-x border-line-subtle text-center text-status-success">{fmt(grand.debit)}</td>
                  <td className="p-3 border-x border-line-subtle text-center text-status-danger">{fmt(grand.credit)}</td>
                  <td className="p-3 border-x border-line-subtle text-center text-status-success">{fmt(grand.closing_debit)}</td>
                  <td className="p-3 border-x border-line-subtle text-center text-status-danger">{fmt(grand.closing_credit)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default TrialBalance;
